import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Link, useParams } from "react-router-dom";
import { Loader2, Send, User } from "lucide-react";
import { useTranslation } from "react-i18next";

import { db } from "../../firebase";

const functions = getFunctions(undefined, "us-central1");

function formatTs(value) {
  if (!value) return "—";
  if (typeof value.toDate === "function") {
    try {
      return value.toDate().toLocaleString();
    } catch {
      return String(value);
    }
  }
  if (typeof value === "object" && value.seconds != null) {
    try {
      return new Date(value.seconds * 1000).toLocaleString();
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function callableErrorMessage(error) {
  const code = error?.code ? String(error.code) : "";
  const msg = error?.message ? String(error.message) : "";
  if (code && msg) return `${code}: ${msg}`;
  return msg || "Request failed.";
}

function isLikelyImageUrl(url) {
  const u = String(url || "").split("?")[0].toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(u);
}

function statusBadgeClass(status) {
  const s = String(status ?? "").trim().toLowerCase();
  if (s === "closed") return "admin1-support-detail__badge admin1-support-detail__badge--closed";
  if (s === "open") return "admin1-support-detail__badge admin1-support-detail__badge--open";
  if (s === "under_review") {
    return "admin1-support-detail__badge admin1-support-detail__badge--under_review";
  }
  if (s === "pending") return "admin1-support-detail__badge admin1-support-detail__badge--pending";
  return "admin1-support-detail__badge admin1-support-detail__badge--default";
}

export default function SupportTicketDetailsPage() {
  const { t } = useTranslation();
  const { ticketId } = useParams();
  const decodedId = ticketId ? decodeURIComponent(ticketId) : "";

  const [ticket, setTicket] = useState(null);
  const [ticketExists, setTicketExists] = useState(true);
  const [ticketLoading, setTicketLoading] = useState(true);
  const [ticketSnapError, setTicketSnapError] = useState("");

  const [messages, setMessages] = useState([]);
  const [messagesError, setMessagesError] = useState("");

  const [replyText, setReplyText] = useState("");
  const [sendWorking, setSendWorking] = useState(false);
  const [closeWorking, setCloseWorking] = useState(false);
  const [actionFeedback, setActionFeedback] = useState("");

  const chatEndRef = useRef(null);

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (!decodedId) {
      setTicketLoading(false);
      setTicketExists(false);
      return undefined;
    }

    setTicketLoading(true);
    setTicketSnapError("");
    const ref = doc(db, "supportTickets", decodedId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setTicket(null);
          setTicketExists(false);
        } else {
          setTicketExists(true);
          setTicket({ id: snap.id, ...snap.data() });
        }
        setTicketLoading(false);
      },
      (err) => {
        console.error("SupportTicketDetails ticket snapshot:", err);
        setTicketSnapError(
          err?.message ||
            t("admin.support.detail.load_ticket_error", {
              defaultValue: "Failed to load ticket",
            })
        );
        setTicketLoading(false);
      }
    );

    return () => unsub();
  }, [decodedId, t]);

  useEffect(() => {
    if (!decodedId) {
      setMessages([]);
      return undefined;
    }

    setMessagesError("");
    const q = query(
      collection(db, "supportTickets", decodedId, "messages"),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setMessages(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );
      },
      (err) => {
        console.error("SupportTicketDetails messages snapshot:", err);
        setMessagesError(
          err?.message ||
            t("admin.support.detail.load_messages_error", {
              defaultValue: "Failed to load messages",
            })
        );
        setMessages([]);
      }
    );

    return () => unsub();
  }, [decodedId, t]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const statusClosed = useMemo(() => {
    const s = String(ticket?.status ?? "").toLowerCase();
    return s === "closed";
  }, [ticket]);

  const canSend = replyText.trim().length > 0 && !statusClosed && !sendWorking;

  async function handleSendReply(e) {
    e.preventDefault();
    if (!decodedId || statusClosed || !canSend) return;

    setSendWorking(true);
    setActionFeedback("");
    try {
      const fn = httpsCallable(functions, "sendSupportMessage");
      await fn({
        ticketId: decodedId,
        text: replyText.trim(),
      });
      setReplyText("");
      requestAnimationFrame(() => scrollToBottom());
    } catch (error) {
      console.error("sendSupportMessage:", error);
      setActionFeedback(callableErrorMessage(error));
    } finally {
      setSendWorking(false);
    }
  }

  async function handleCloseTicket() {
    if (!decodedId) return;
    setCloseWorking(true);
    setActionFeedback("");
    try {
      const fn = httpsCallable(functions, "closeSupportTicket");
      await fn({ ticketId: decodedId });
    } catch (error) {
      console.error("closeSupportTicket:", error);
      setActionFeedback(callableErrorMessage(error));
    } finally {
      setCloseWorking(false);
    }
  }

  if (!decodedId) {
    return (
      <div className="admin1-dashboard">
        <div className="admin1-state-card">
          {t("admin.support.detail.invalid_ticket_id", {
            defaultValue: "Invalid ticket id.",
          })}
        </div>
        <Link className="admin1-support-detail__back" to="/admin1/support">
          {t("admin.support.detail.back_to_support", {
            defaultValue: "← Back to Support",
          })}
        </Link>
      </div>
    );
  }

  return (
    <div className="admin1-dashboard admin1-support-detail">
      {ticketSnapError ? (
        <div className="admin1-state-card admin1-state-card--error">
          {ticketSnapError}
        </div>
      ) : null}

      {ticketLoading ? (
        <div className="admin1-state-card">
          {t("admin.support.detail.loading_ticket", { defaultValue: "Loading ticket..." })}
        </div>
      ) : !ticketExists ? (
        <div className="admin1-state-card">
          {t("admin.support.detail.ticket_not_found", { defaultValue: "Ticket not found." })}
        </div>
      ) : (
        <div className="admin1-support-detail__viewport">
          <header className="admin1-support-detail__ticket-header">
            <div className="admin1-support-detail__ticket-header-inner">
              <div className="admin1-support-detail__ticket-header-main">
                <Link className="admin1-support-detail__back" to="/admin1/support">
                  {t("admin.support.detail.back_to_support", {
                    defaultValue: "← Back to Support",
                  })}
                </Link>
                <h1 className="admin1-support-detail__ticket-heading">
                  {t("admin.support.detail.title", { defaultValue: "Support conversation" })}
                </h1>
                <div className="admin1-support-detail__ticket-meta">
                  <span className="admin1-support-detail__meta-item">
                    <span className="admin1-support-detail__meta-label">
                      {t("support.ticket", { defaultValue: "Ticket" })}
                    </span>
                    <span className="admin1-support-detail__meta-value mono-wrap">
                      {decodedId}
                    </span>
                  </span>
                  <span className="admin1-support-detail__meta-item">
                    <span className="admin1-support-detail__meta-label">
                      {t("admin.support.fields.user", { defaultValue: "User" })}
                    </span>
                    <span className="admin1-support-detail__meta-value">
                      {ticket.uid ? (
                        <Link
                          to={`/admin1/user/${encodeURIComponent(String(ticket.uid))}`}
                          className="admin1-support-detail__meta-link"
                        >
                          {String(ticket.uid)}
                        </Link>
                      ) : (
                        "—"
                      )}
                    </span>
                  </span>
                  <span className="admin1-support-detail__meta-item">
                    <span className="admin1-support-detail__meta-label">
                      {t("admin.support.fields.type", { defaultValue: "Type" })}
                    </span>
                    <span className="admin1-support-detail__meta-value">
                      {ticket.type != null ? String(ticket.type) : "—"}
                    </span>
                  </span>
                  <span className="admin1-support-detail__meta-item">
                    <span className="admin1-support-detail__meta-label">
                      {t("admin.support.fields.priority", { defaultValue: "Priority" })}
                    </span>
                    <span className="admin1-support-detail__meta-value">
                      {ticket.priority != null ? String(ticket.priority) : "—"}
                    </span>
                  </span>
                  <span className="admin1-support-detail__meta-item">
                    <span className="admin1-support-detail__meta-label">
                      {t("admin.support.fields.status", { defaultValue: "Status" })}
                    </span>
                    <span className={statusBadgeClass(ticket.status)}>
                      {ticket.status
                        ? t(`support.status.${String(ticket.status)}`, {
                            defaultValue: String(ticket.status),
                          })
                        : "—"}
                    </span>
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="admin1-support-detail__close-btn"
                disabled={closeWorking || statusClosed}
                onClick={() => void handleCloseTicket()}
              >
                {closeWorking ? (
                  <Loader2
                    className="admin1-support-detail__btn-spinner"
                    size={18}
                    aria-hidden
                  />
                ) : null}
                <span>
                  {closeWorking
                    ? t("admin.support.detail.closing", { defaultValue: "Closing..." })
                    : t("support.close_ticket", { defaultValue: "Close ticket" })}
                </span>
              </button>
            </div>
          </header>

          {messagesError ? (
            <div className="admin1-state-card admin1-state-card--error">
              {messagesError}
            </div>
          ) : null}

          <div className="admin1-support-detail__scroll-area">
            <div className="admin1-support-detail__chat-max">
              <section
                className="admin1-support-detail__chat"
                aria-label={t("support.messages", { defaultValue: "Messages" })}
              >
                {messages.length === 0 ? (
                  <div className="admin1-support-detail__empty">
                    {t("support.no_messages_yet", { defaultValue: "No messages yet" })}
                  </div>
                ) : (
                  <ul className="admin1-support-detail__messages">
                    {messages.map((m) => {
                      const role = String(m.senderRole || "").toLowerCase();
                      const isAdmin = role === "admin";
                      const text = String(m.text ?? "").trim();
                      const attachmentUrl = m.attachmentUrl
                        ? String(m.attachmentUrl).trim()
                        : "";

                      return (
                        <li
                          key={m.id}
                          className={`admin1-support-detail__msg ${
                            isAdmin
                              ? "admin1-support-detail__msg--admin"
                              : "admin1-support-detail__msg--user"
                          }`}
                        >
                          {isAdmin ? (
                            <div className="admin1-support-detail__admin-wrap">
                              <span className="admin1-support-detail__sender-label">
                                {t("admin.support.role_admin", { defaultValue: "Admin" })}
                              </span>
                              <div className="admin1-support-detail__bubble admin1-support-detail__bubble--admin">
                                {text ? (
                                  <p className="admin1-support-detail__text">{text}</p>
                                ) : null}
                                {attachmentUrl ? (
                                  <div className="admin1-support-detail__attach">
                                    {isLikelyImageUrl(attachmentUrl) ? (
                                      <a
                                        href={attachmentUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="admin1-support-detail__attach-link"
                                      >
                                        <img
                                          src={attachmentUrl}
                                          alt=""
                                          className="admin1-support-detail__attach-img"
                                        />
                                      </a>
                                    ) : (
                                      <a
                                        href={attachmentUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="admin1-support-detail__file-link"
                                      >
                                        {m.attachmentName
                                          ? String(m.attachmentName)
                                          : t("support.attachment", {
                                              defaultValue: "Attachment",
                                            })}
                                      </a>
                                    )}
                                  </div>
                                ) : null}
                                <div className="admin1-support-detail__time">
                                  {formatTs(m.createdAt)}
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="admin1-support-detail__user-row">
                              <div
                                className="admin1-support-detail__avatar"
                                aria-hidden
                              >
                                <User size={18} strokeWidth={2} />
                              </div>
                              <div className="admin1-support-detail__user-col">
                                <span className="admin1-support-detail__sender-label admin1-support-detail__sender-label--user">
                                  {t("admin.support.fields.user", { defaultValue: "User" })}
                                </span>
                                <div className="admin1-support-detail__bubble admin1-support-detail__bubble--user">
                                  {text ? (
                                    <p className="admin1-support-detail__text">{text}</p>
                                  ) : null}
                                  {attachmentUrl ? (
                                    <div className="admin1-support-detail__attach">
                                      {isLikelyImageUrl(attachmentUrl) ? (
                                        <a
                                          href={attachmentUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="admin1-support-detail__attach-link"
                                        >
                                          <img
                                            src={attachmentUrl}
                                            alt=""
                                            className="admin1-support-detail__attach-img"
                                          />
                                        </a>
                                      ) : (
                                        <a
                                          href={attachmentUrl}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="admin1-support-detail__file-link"
                                        >
                                          {m.attachmentName
                                            ? String(m.attachmentName)
                                            : t("support.attachment", {
                                                defaultValue: "Attachment",
                                              })}
                                        </a>
                                      )}
                                    </div>
                                  ) : null}
                                  <div className="admin1-support-detail__time">
                                    {formatTs(m.createdAt)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                    <li ref={chatEndRef} aria-hidden className="admin1-support-detail__scroll-anchor" />
                  </ul>
                )}
              </section>
            </div>
          </div>

          <div className="admin1-support-detail__composer">
            <form
              className="admin1-support-detail__composer-inner"
              onSubmit={(e) => void handleSendReply(e)}
            >
              <div className="admin1-support-detail__composer-field">
                <textarea
                  id="support-reply"
                  className="support-reply-input"
                  rows={1}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={
                    statusClosed
                      ? t("admin.support.detail.ticket_closed_placeholder", {
                          defaultValue: "Ticket is closed",
                        })
                      : t("admin.support.detail.reply_placeholder", {
                          defaultValue: "Type your reply...",
                        })
                  }
                  disabled={statusClosed || sendWorking}
                />
                <button
                  type="submit"
                  className="admin1-support-detail__send-btn"
                  disabled={!canSend}
                  aria-label={t("support.send_reply", { defaultValue: "Send reply" })}
                >
                  {sendWorking ? (
                    <Loader2
                      className="admin1-support-detail__btn-spinner"
                      size={20}
                      aria-hidden
                    />
                  ) : (
                    <Send size={20} strokeWidth={2} aria-hidden />
                  )}
                </button>
              </div>
            </form>
          </div>

          {actionFeedback ? (
            <div className="admin1-vq__feedback admin1-support-detail__feedback">
              {actionFeedback}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
